import { createBrowserRouter } from 'react-router';

import { AppLayout } from '@/components/app-layout';
import { RouteErrorFallback } from '@/components/error-fallback';
import { AnalyticsPage } from '@/features/analytics/analytics-page';
import { RequireAuth } from '@/features/auth/guards';
import { authRoutes } from '@/features/auth/routes';
import { CalendarPage } from '@/features/calendar/calendar-page';
import { CompetitorsPage } from '@/features/competitors/competitors-page';
import { connectRoute } from '@/features/connect/routes';
import { DraftsPage } from '@/features/flows/drafts-page';
import { FlowEditorPage } from '@/features/flows/editor/flow-editor-page';
import { FlowsIndexPage } from '@/features/flows/flows-index-page';
import { ResultsPage } from '@/features/flows/results/results-page';
import { RunningPage } from '@/features/flows/running/running-page';
import { RunsPage } from '@/features/flows/runs/runs-page';
import { ComingSoonPage } from '@/features/home/coming-soon-page';
import { HomePage } from '@/features/home/home-page';
import { inviteRoute } from '@/features/invite/routes';
import { CollectionPage } from '@/features/media/collection-page';
import { LibraryPage } from '@/features/media/library-page';
import { MediaLayout } from '@/features/media/media-layout';
import { AllNotificationsPage } from '@/features/notifications/notifications-page';
import { RequireOnboarded } from '@/features/onboarding/guards';
import { onboardingRoutes } from '@/features/onboarding/routes';
import { PersonaPage } from '@/features/personas/persona-page';
import { PersonasPage } from '@/features/personas/personas-page';
import { AccountPage } from '@/features/settings/account-page';
import { ApiPage } from '@/features/settings/api-page';
import { BillingPage } from '@/features/settings/billing-page';
import { BrandProfilePage } from '@/features/settings/brand-profile-page';
import { CompetitorsSettingsPage } from '@/features/settings/competitors-page';
import { ConnectedAccountsPage } from '@/features/settings/connected-accounts-page';
import { GeneralPage } from '@/features/settings/general-page';
import { NotificationsPage } from '@/features/settings/notifications-page';
import { SettingsPage } from '@/features/settings/settings-page';
import { shareRoute } from '@/features/share/routes';
import { TeamPage } from '@/features/team/team-page';
import { unsubscribeRoute } from '@/features/unsubscribe/routes';

import { RootLayout } from './root-layout';

/** Sidebar destinations that aren't built yet. */
const COMING_SOON = [{ path: 'flows/scheduled', title: 'Scheduled' }];

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      ...authRoutes,
      // Unguarded: reachable signed out, and before (instead of) onboarding.
      inviteRoute,
      // Signed in, but not behind onboarding: agents send people here to approve them.
      connectRoute,
      unsubscribeRoute,
      shareRoute,
      onboardingRoutes,
      {
        // Signed in and onboarded: anyone mid-onboarding goes back to their step.
        element: (
          <RequireAuth>
            <RequireOnboarded>
              <AppLayout />
            </RequireOnboarded>
          </RequireAuth>
        ),
        // Catches render and loader errors from any child route and keeps the
        // layout (navigation) on screen.
        errorElement: <RouteErrorFallback />,
        children: [
          { index: true, element: <HomePage /> },
          ...COMING_SOON.map(({ path, title }) => ({
            path,
            element: <ComingSoonPage title={title} />,
          })),
          {
            path: 'media',
            element: <MediaLayout />,
            children: [
              { index: true, element: <LibraryPage /> },
              { path: 'collections/:collectionId', element: <CollectionPage /> },
            ],
          },
          { path: 'flows', element: <FlowsIndexPage /> },
          { path: 'flows/drafts', element: <DraftsPage /> },
          { path: 'flows/running', element: <RunningPage /> },
          { path: 'flows/:flowId', element: <FlowEditorPage /> },
          { path: 'flows/:flowId/runs', element: <RunsPage /> },
          { path: 'flows/:flowId/results', element: <ResultsPage /> },
          { path: 'competitors', element: <CompetitorsPage /> },
          { path: 'personas', element: <PersonasPage /> },
          { path: 'personas/:personaId', element: <PersonaPage /> },
          { path: 'calendar', element: <CalendarPage /> },
          { path: 'analytics', element: <AnalyticsPage /> },
          { path: 'team', element: <TeamPage /> },
          { path: 'notifications', element: <AllNotificationsPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'settings/general', element: <GeneralPage /> },
          { path: 'settings/brand-profile', element: <BrandProfilePage /> },
          { path: 'settings/competitors', element: <CompetitorsSettingsPage /> },
          { path: 'settings/connected-accounts', element: <ConnectedAccountsPage /> },
          { path: 'settings/billing', element: <BillingPage /> },
          { path: 'settings/api', element: <ApiPage /> },
          { path: 'settings/account', element: <AccountPage /> },
          { path: 'settings/notifications', element: <NotificationsPage /> },
          { path: '*', element: <RouteErrorFallback notFound /> },
        ],
      },
    ],
  },
]);
